import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  InputAdornment,
  TextField,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import FilterSelectInput from 'src/common/FilterSelectInput';
import OperatorListTable from 'src/components/operators/OperatorListTable';
import NewOperator from 'src/components/operators/NewOperator';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';

const Operators = ({ location }) => {
  const theme = useTheme();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter();
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState('');

  return (
    <>
      <Helmet>
        <title>Operators | Retrack FMS</title>
      </Helmet>

      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                OPERATORS
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Manage site operators and their assignments.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                placeholder="Search operators..."
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  width: { xs: '100%', md: 320 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={setSiteFilter}
                  sx={{ width: { xs: '100%', md: 220 }, mr: 0 }}
                />
              )}

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              >
                Add Operator
              </Button>
            </Stack>
          </Stack>

          <NewOperator
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <OperatorListTable
              refreshData={location.state?.fromDashboard}
              searchText={searchText}
              siteFilter={siteFilter}
            />
          </Box>

        </Container>
      </Box>
    </>
  );
};

Operators.propTypes = {
  location: PropTypes.object
};

export default Operators;
